import { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import { supabase } from '../services/supabase'
import Button from './Button'

interface AdminUser {
    id: string
    email: string
    nome: string
    role: 'super_admin' | 'admin'
    ativo: boolean
}

interface ModalAdminUsuarioProps {
    isOpen: boolean
    onClose: () => void
    onSuccess: () => void
    adminUser?: AdminUser | null
    onShowToast: (type: 'success' | 'error', title: string, message?: string) => void
}

export default function ModalAdminUsuario({
    isOpen,
    onClose,
    onSuccess,
    adminUser,
    onShowToast
}: ModalAdminUsuarioProps) {
    const [formData, setFormData] = useState({
        email: '',
        nome: '',
        role: 'admin' as 'super_admin' | 'admin',
        ativo: true,
        password: ''
    })
    const [loading, setLoading] = useState(false)
    const [errors, setErrors] = useState<Record<string, string>>({})

    useEffect(() => {
        if (adminUser) {
            setFormData({
                email: adminUser.email,
                nome: adminUser.nome,
                role: adminUser.role,
                ativo: adminUser.ativo,
                password: ''
            })
        } else {
            setFormData({
                email: '',
                nome: '',
                role: 'admin',
                ativo: true,
                password: ''
            })
        }
        setErrors({})
    }, [adminUser, isOpen])

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target
        const checked = (e.target as HTMLInputElement).checked

        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }))

        // Limpar erro do campo quando o usuário começar a digitar
        if (errors[name]) {
            setErrors(prev => {
                const newErrors = { ...prev }
                delete newErrors[name]
                return newErrors
            })
        }
    }

    const validateForm = () => {
        const newErrors: Record<string, string> = {}

        if (!formData.email.trim()) {
            newErrors.email = 'Email é obrigatório'
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Email inválido'
        }

        if (!formData.nome.trim()) {
            newErrors.nome = 'Nome é obrigatório'
        }

        if (!adminUser && !formData.password.trim()) {
            newErrors.password = 'Senha é obrigatória para novos usuários'
        } else if (formData.password && formData.password.length < 6) {
            newErrors.password = 'Senha deve ter pelo menos 6 caracteres'
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!validateForm()) {
            return
        }

        setLoading(true)
        setErrors({})

        try {
            if (adminUser) {
                // Atualizar usuário admin existente
                const { error } = await supabase
                    .from('admin_users')
                    .update({
                        nome: formData.nome,
                        role: formData.role,
                        ativo: formData.ativo,
                        updated_at: new Date().toISOString()
                    })
                    .eq('id', adminUser.id)

                if (error) throw error

                // Nota: Atualização de senha requer service role key ou função serverless
                // Para atualizar a senha, use o painel do Supabase ou implemente uma função serverless
                const message = formData.password 
                    ? 'Usuário atualizado! Nota: Para alterar a senha, use o painel do Supabase.'
                    : 'Usuário administrador atualizado com sucesso!'
                
                onShowToast('success', 'Sucesso!', message)
            } else {
                // Criar novo usuário admin usando Edge Function
                // A Edge Function usa service role key no backend para criar o usuário no Auth
                const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
                const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

                if (!supabaseUrl || !supabaseAnonKey) {
                    throw new Error('Configuração do Supabase não encontrada. Verifique as variáveis de ambiente.')
                }

                try {
                    const response = await fetch(`${supabaseUrl}/functions/v1/create-admin-user`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${supabaseAnonKey}`,
                            'apikey': supabaseAnonKey
                        },
                        body: JSON.stringify({
                            email: formData.email,
                            password: formData.password,
                            nome: formData.nome,
                            role: formData.role,
                            ativo: formData.ativo
                        })
                    })

                    // Verificar se a resposta é válida
                    if (!response.ok) {
                        // Tentar ler a resposta como JSON
                        let errorMessage = 'Erro ao criar usuário administrador'
                        try {
                            const errorData = await response.json()
                            errorMessage = errorData.error || errorMessage
                        } catch {
                            // Se não conseguir ler JSON, usar status text
                            errorMessage = `Erro ${response.status}: ${response.statusText || 'Falha na requisição'}`
                        }

                        // Se for erro 404, a função não foi deployada
                        if (response.status === 404) {
                            throw new Error(
                                'Edge Function não encontrada. ' +
                                'Por favor, faça o deploy da função "create-admin-user" no Supabase. ' +
                                'Consulte o arquivo INSTRUCOES_DEPLOY_EDGE_FUNCTION.md para mais detalhes.'
                            )
                        }

                        throw new Error(errorMessage)
                    }

                    const result = await response.json()

                    if (!result.success) {
                        throw new Error(result.error || 'Erro ao criar usuário administrador')
                    }

                    onShowToast(
                        'success', 
                        'Usuário administrador criado com sucesso!', 
                        `O administrador ${formData.nome} foi criado automaticamente no Supabase Auth e já pode fazer login com o email ${formData.email} e a senha informada.`
                    )
                } catch (fetchError: any) {
                    // Se for erro de rede (Failed to fetch), oferecer alternativa
                    if (fetchError.message?.includes('Failed to fetch') || fetchError.name === 'TypeError') {
                        // Fallback: criar apenas na tabela admin_users e informar sobre criação manual no Auth
                        console.warn('Edge Function não disponível, criando apenas na tabela admin_users')
                        
                        // Verificar se o email já existe
                        const { data: existingAdmin } = await supabase
                            .from('admin_users')
                            .select('id')
                            .eq('email', formData.email)
                            .single()

                        if (existingAdmin) {
                            throw new Error('Já existe um administrador com este email')
                        }

                        // Criar apenas na tabela admin_users
                        const { error: adminError } = await supabase
                            .from('admin_users')
                            .insert({
                                email: formData.email,
                                nome: formData.nome,
                                role: formData.role,
                                ativo: formData.ativo
                            })

                        if (adminError) throw adminError

                        // Informar sobre criação manual no Auth
                        throw new Error(
                            `Usuário criado na tabela admin_users, mas a Edge Function não está disponível. ` +
                            `IMPORTANTE: Crie manualmente a conta no Supabase Auth (Authentication > Users) ` +
                            `com email: ${formData.email} e senha: ${formData.password}. ` +
                            `Para automatizar isso, faça o deploy da Edge Function (veja INSTRUCOES_DEPLOY_EDGE_FUNCTION.md)`
                        )
                    }
                    // Re-lançar outros erros
                    throw fetchError
                }
            }

            onSuccess()
        } catch (error: any) {
            console.error('Erro ao salvar usuário administrador:', error)
            onShowToast('error', 'Erro ao salvar usuário administrador', error.message || 'Erro desconhecido')
        } finally {
            setLoading(false)
        }
    }

    if (!isOpen) return null

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full border border-gray-200 dark:border-gray-700">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                        {adminUser ? 'Editar Administrador' : 'Novo Administrador'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Content */}
                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    {/* Email */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Email *
                        </label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            disabled={!!adminUser}
                            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 ${
                                errors.email
                                    ? 'border-red-300 dark:border-red-600'
                                    : 'border-gray-300 dark:border-gray-600'
                            } ${adminUser ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : ''}`}
                            placeholder="admin@exemplo.com"
                        />
                        {errors.email && (
                            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.email}</p>
                        )}
                    </div>

                    {/* Nome */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Nome *
                        </label>
                        <input
                            type="text"
                            name="nome"
                            value={formData.nome}
                            onChange={handleInputChange}
                            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 ${
                                errors.nome
                                    ? 'border-red-300 dark:border-red-600'
                                    : 'border-gray-300 dark:border-gray-600'
                            }`}
                            placeholder="Nome completo"
                        />
                        {errors.nome && (
                            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.nome}</p>
                        )}
                    </div>

                    {/* Role */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Permissão *
                        </label>
                        <select
                            name="role"
                            value={formData.role}
                            onChange={handleInputChange}
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        >
                            <option value="admin">Administrador</option>
                            <option value="super_admin">Super Administrador</option>
                        </select>
                    </div>

                    {/* Senha (apenas para novos usuários ou se quiser alterar) */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            {adminUser ? 'Nova Senha (deixe em branco para manter)' : 'Senha *'}
                        </label>
                        <input
                            type="password"
                            name="password"
                            value={formData.password}
                            onChange={handleInputChange}
                            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 ${
                                errors.password
                                    ? 'border-red-300 dark:border-red-600'
                                    : 'border-gray-300 dark:border-gray-600'
                            }`}
                            placeholder={adminUser ? 'Deixe em branco para manter a senha atual' : 'Mínimo 6 caracteres'}
                        />
                        {errors.password && (
                            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.password}</p>
                        )}
                    </div>

                    {/* Ativo */}
                    <div className="flex items-center">
                        <input
                            type="checkbox"
                            name="ativo"
                            id="ativo"
                            checked={formData.ativo}
                            onChange={handleInputChange}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600"
                        />
                        <label htmlFor="ativo" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                            Usuário ativo
                        </label>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <Button
                            type="button"
                            variant="btn_secondary"
                            onClick={onClose}
                            disabled={loading}
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            variant="btn_add"
                            loading={loading}
                            disabled={loading}
                        >
                            {adminUser ? 'Atualizar' : 'Criar'} Administrador
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    )
}

